import { useCallback, useEffect, useRef } from "react"
import { useClient, useQuery } from "urql"

import {
  usePDFPendingUpdate,
  useSetPDFError,
  useSetPDFGenerating,
  useSetPDFUrl,
} from "../PurchaseOrder/contexts/PDFContext"
import type { PurchaseOrderIds } from "../PurchaseOrder/types"

import type { FieldGroupEnum, GetOrderPdfQuery, GetOrderPdfQueryVariables } from "@/graphql/codegen/graphql"
import { GetOrderPdfDocument } from "@/graphql/codegen/graphql"

type PendingPromiseRef = {
  resolve: (value: string) => void
  reject: (error: Error) => void
} | null

interface CreateFileUrlProps {
  requireExternalSystemId?: boolean
  fieldGroup?: FieldGroupEnum
}

interface UsePurchaseOrderFileReturn {
  signedUrl: string | null
  createFileUrl: (props: CreateFileUrlProps) => Promise<string>
  isFetching: boolean
}

const getSignedUrl = (data: GetOrderPdfQuery | undefined): string | null => {
  return data?.order?.getOrderPdf?.__typename === "GetOrderPdfSuccess"
    ? (data.order.getOrderPdf.signedUrl ?? null)
    : null
}

export const usePurchaseOrderFile = ({ orderId, externalSystemId }: PurchaseOrderIds): UsePurchaseOrderFileReturn => {
  const client = useClient()

  const [{ data, fetching, error }] = useQuery<GetOrderPdfQuery, GetOrderPdfQueryVariables>({
    query: GetOrderPdfDocument,
    variables: { input: { orderId: orderId ?? 0 } },
    pause: true, // Always paused - makes it lazy
  })

  const setGenerating = useSetPDFGenerating()
  const setURL = useSetPDFUrl()
  const setError = useSetPDFError()
  const pendingUpdate = usePDFPendingUpdate()

  // Use refs to track pending promises
  const pendingPromiseRef = useRef<PendingPromiseRef>(null)

  const resolvePendingPromise = useCallback((data: GetOrderPdfQuery) => {
    if (pendingPromiseRef.current) {
      const signedUrl = getSignedUrl(data)

      if (signedUrl) {
        pendingPromiseRef.current.resolve(signedUrl)
      } else {
        pendingPromiseRef.current.reject(new Error("Failed to generate or retrieve the purchase order PDF."))
      }

      pendingPromiseRef.current = null
    }
  }, [])

  const rejectPendingPromise = useCallback((error: Error) => {
    if (pendingPromiseRef.current) {
      pendingPromiseRef.current.reject(error)
      pendingPromiseRef.current = null
    }
  }, [])

  // Handle loading state
  useEffect(() => {
    setGenerating(!pendingUpdate && fetching)
  }, [fetching, setGenerating, pendingUpdate])

  // Handle response
  useEffect(() => {
    if (error) {
      setError(error.message)
      rejectPendingPromise(new Error(error.message))
      return
    }

    if (data) {
      const signedUrl = getSignedUrl(data)
      setURL(signedUrl)
      resolvePendingPromise(data)
    }
  }, [data, error, setURL, setError, rejectPendingPromise, resolvePendingPromise])

  const createFileUrl = useCallback(
    async ({ requireExternalSystemId = true, fieldGroup }: CreateFileUrlProps = {}): Promise<string> => {
      if (requireExternalSystemId && !externalSystemId) {
        throw new Error("AS400 ID is required to generate the purchase order PDF.")
      }
      if (!orderId) {
        throw new Error("Order ID is required to generate the purchase order PDF.")
      }

      setGenerating(true)

      const result = await client
        .query(
          GetOrderPdfDocument,
          {
            input: {
              orderId: orderId ?? 0,
              ...(fieldGroup && { fieldGroup }),
            },
          },
          { requestPolicy: "network-only" }
        )
        .toPromise()

      if (result.error) {
        setError(result.error.message)
        setGenerating(false)
        throw new Error(result.error.message)
      }

      if (result.data) {
        const signedUrl = getSignedUrl(result.data)
        setURL(signedUrl)
        setGenerating(false)

        if (signedUrl) {
          return signedUrl
        } else {
          throw new Error("Failed to generate or retrieve the purchase order PDF.")
        }
      }

      setGenerating(false)
      throw new Error("Failed to generate or retrieve the purchase order PDF.")
    },
    [client, externalSystemId, orderId, setGenerating, setError, setURL]
  )

  const signedUrl = getSignedUrl(data)

  return { signedUrl, createFileUrl, isFetching: fetching }
}
